'use client';

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface InlineHelpProps {
  term: string;
  description: string;
  learnMoreHref?: string;
  children?: ReactNode;
}

const POSITION_CLASSES = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
} as const;

type Placement = keyof typeof POSITION_CLASSES;

function computePlacement(trigger: HTMLElement | null): Placement {
  if (typeof window === 'undefined' || !trigger) return 'top';
  const rect = trigger.getBoundingClientRect();
  const spaceAbove = rect.top;
  const spaceBelow = window.innerHeight - rect.bottom;
  if (spaceAbove < 160 && spaceBelow > spaceAbove) return 'bottom';
  return 'top';
}

export function InlineHelp({
  term,
  description,
  learnMoreHref,
  children,
}: InlineHelpProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>('top');
  const popoverId = useId();
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    if (!isOpen) return;
    setPlacement(computePlacement(triggerRef.current));
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
    function handleScroll() {
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      close();
    }
  }

  const positionClasses = POSITION_CLASSES[placement];
  const arrowUp = placement === 'top';
  const arrowClass = arrowUp
    ? 'bottom-[-4px] left-1/2 -translate-x-1/2 border-b-0 border-r-0'
    : 'top-[-4px] left-1/2 -translate-x-1/2 border-l-0 border-t-0';

  const describedBy = isOpen ? popoverId : undefined;

  return (
    <span
      ref={containerRef}
      className="relative inline-flex items-center gap-1 align-baseline"
      data-tour="inline-help"
    >
      {children ? <span className="inline-flex items-center gap-1">{children}</span> : null}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        onKeyDown={handleKeyDown}
        aria-label={`Hilfe zu ${term}`}
        aria-expanded={isOpen}
        aria-describedby={describedBy}
        aria-controls={isOpen ? popoverId : undefined}
        data-testid="inline-help-trigger"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[var(--k-color-border-default)] bg-[var(--k-color-surface-muted)] text-[10px] font-bold leading-none text-[var(--k-color-text-muted)] transition hover:border-[var(--k-color-accent)] hover:bg-[var(--k-color-accent-soft)] hover:text-[var(--k-color-text-link)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-accent)]"
      >
        <span aria-hidden="true">?</span>
      </button>
      {isOpen ? (
        <span
          id={popoverId}
          role="tooltip"
          data-testid="inline-help-popover"
          className={`absolute z-50 w-64 max-w-[calc(100vw-2rem)] rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-3 py-2 text-xs shadow-[var(--k-shadow-lg)] ${positionClasses}`}
        >
          <span
            aria-hidden="true"
            className={`absolute h-2 w-2 rotate-45 border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] ${arrowClass}`}
          />
          <span className="block font-semibold text-[var(--k-color-text-primary)]">{term}</span>
          <span className="mt-1 block leading-snug text-[var(--k-color-text-secondary)]">
            {description}
          </span>
          {learnMoreHref ? (
            <a
              href={learnMoreHref}
              className="mt-1.5 inline-block text-[11px] font-semibold text-[var(--k-color-text-link)] hover:underline"
            >
              Mehr erfahren →
            </a>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

export interface InlineHelpTermProps {
  term: string;
  description: string;
  learnMoreHref?: string;
}

export function InlineHelpTerm({ term, description, learnMoreHref }: InlineHelpTermProps) {
  return (
    <InlineHelp term={term} description={description} learnMoreHref={learnMoreHref}>
      <span className="font-medium text-[var(--k-color-text-primary)]">{term}</span>
    </InlineHelp>
  );
}
